import React from 'react';
import { TrendingUp, TrendingDown, Activity, Info } from 'lucide-react';
import { useJournalStore } from '../../store/journalStore';

export function JournalImpacts() {
  const { selectedBehaviors, getImpactAnalysis } = useJournalStore();

  // If no behaviors are selected, show an empty state
  if (selectedBehaviors.length === 0) {
    return (
      <div className="max-w-4xl mx-auto">
        <div className="theme-card p-6 text-center space-y-4">
          <div className="flex items-center justify-center gap-2 text-muted-foreground">
            <Info className="w-5 h-5" />
            <p>No behaviors selected for analysis</p>
          </div>
          <p className="text-sm text-muted-foreground">
            Add behaviors from the Behaviors tab to see their impact on your metrics.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto space-y-6">
      <div className="theme-card p-6 space-y-6">
        <div className="flex items-center gap-3">
          <div className="relative">
            <Activity className="w-5 h-5 text-primary" />
            <div className="absolute inset-0 bg-primary/20 blur-xl rounded-full" />
          </div>
          <h2 className="text-lg font-semibold">Behavior Impacts</h2>
        </div>

        <div className="space-y-4">
          {selectedBehaviors.map((behavior) => {
            const impact = getImpactAnalysis(behavior.id);
            
            // Skip rendering if impact data is not available
            if (!impact) return null;

            return (
              <div key={behavior.id} className="bg-muted/30 p-4 rounded-lg space-y-3">
                <h3 className="font-medium">{behavior.name}</h3>
                
                <div className="grid grid-cols-3 gap-4">
                  {/* Recovery Impact */}
                  {typeof impact.recovery === 'number' && (
                    <div className="space-y-1">
                      <div className="text-sm text-muted-foreground">Recovery</div>
                      <div className="flex items-center gap-1">
                        {impact.recovery > 0 ? (
                          <TrendingUp className="w-4 h-4 text-success" />
                        ) : (
                          <TrendingDown className="w-4 h-4 text-destructive" />
                        )}
                        <span className={impact.recovery > 0 ? 'text-success' : 'text-destructive'}>
                          {Math.abs(impact.recovery).toFixed(1)}%
                        </span>
                      </div>
                    </div>
                  )}

                  {/* Strain Impact */}
                  {typeof impact.strain === 'number' && (
                    <div className="space-y-1">
                      <div className="text-sm text-muted-foreground">Strain</div>
                      <div className="flex items-center gap-1">
                        {impact.strain > 0 ? (
                          <TrendingUp className="w-4 h-4 text-success" />
                        ) : (
                          <TrendingDown className="w-4 h-4 text-destructive" />
                        )}
                        <span className={impact.strain > 0 ? 'text-success' : 'text-destructive'}>
                          {Math.abs(impact.strain).toFixed(1)}%
                        </span>
                      </div>
                    </div>
                  )}

                  {/* Sleep Impact */}
                  {typeof impact.sleep === 'number' && (
                    <div className="space-y-1">
                      <div className="text-sm text-muted-foreground">Sleep</div>
                      <div className="flex items-center gap-1">
                        {impact.sleep > 0 ? (
                          <TrendingUp className="w-4 h-4 text-success" />
                        ) : (
                          <TrendingDown className="w-4 h-4 text-destructive" />
                        )}
                        <span className={impact.sleep > 0 ? 'text-success' : 'text-destructive'}>
                          {Math.abs(impact.sleep).toFixed(1)}%
                        </span>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}